import React, { useMemo, useState } from 'react';
import {
  CartesianGrid,
  ComposedChart,
  Area,
  Line,
  ResponsiveContainer,
  Scatter,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { fmtCurrencyPrecise } from '../cashReserves/helpers';

const COLORS = [
  '#1f77b4',
  '#2ca02c',
  '#ff7f0e',
  '#17becf',
  '#d62728',
  '#9467bd',
  '#8c564b',
  '#e377c2',
  '#7f7f7f',
  '#bcbd22',
  '#003f5c',
  '#665191',
];

const isoDateToUtcTimestamp = (isoDate) => {
  const text = String(isoDate || '');
  const match = text.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!match) return Number.NaN;
  const year = Number(match[1]);
  const monthIndex = Number(match[2]) - 1;
  const day = Number(match[3]);
  // Noon UTC avoids local-timezone rollover displaying the previous date.
  return Date.UTC(year, monthIndex, day, 12, 0, 0, 0);
};

const formatShortDate = (value) => {
  if (value == null || value === '') return '';
  const timestamp =
    typeof value === 'number'
      ? value
      : isoDateToUtcTimestamp(String(value).split('T')[0]);
  const dt = new Date(timestamp);
  if (Number.isNaN(dt.getTime())) return String(value);
  return dt.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: '2-digit',
    timeZone: 'UTC',
  });
};

const formatCurrencyNoCents = (value) => {
  const amount = Number(value || 0);
  if (!Number.isFinite(amount)) return '$0';
  return amount.toLocaleString('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  });
};

const firstOfMonthUtc = (timestamp) => {
  const dt = new Date(timestamp);
  return Date.UTC(dt.getUTCFullYear(), dt.getUTCMonth(), 1, 12, 0, 0, 0);
};

const monthStartTicks = (minTs, maxTs) => {
  if (!Number.isFinite(minTs) || !Number.isFinite(maxTs)) return [];
  const ticks = [];
  let cursor = firstOfMonthUtc(minTs);
  const maxMonthStart = firstOfMonthUtc(maxTs);
  while (cursor <= maxMonthStart) {
    ticks.push(cursor);
    const dt = new Date(cursor);
    cursor = Date.UTC(dt.getUTCFullYear(), dt.getUTCMonth() + 1, 1);
  }
  return ticks;
};

const valueForAccount = (point, accountNumber) => {
  const values = point?.account_values || {};
  const raw = values[accountNumber];
  return Number.isFinite(raw) ? raw : 0;
};

const ChartTooltip = ({ active, payload, label, accountMeta, accountKeys }) => {
  if (!active || !payload || payload.length === 0) return null;
  const row = payload[0]?.payload || {};
  const byKey = {};
  payload.forEach((entry) => {
    byKey[entry.dataKey] = entry.value;
  });

  const accountRows = accountKeys
    .map((accountNumber) => {
      const key = `acct_${accountNumber}`;
      const value = byKey[key];
      if (!Number.isFinite(value) || value <= 0) return null;
      return {
        accountNumber,
        label: accountMeta[accountNumber]?.label || accountNumber,
        value,
        color: accountMeta[accountNumber]?.color || '#6c757d',
      };
    })
    .filter(Boolean)
    .sort((a, b) => b.value - a.value);

  const chartTotal = Number(row.chart_total_value ?? byKey.chart_total_value ?? 0);
  const familyTotal = Number(row.family_total_value ?? byKey.family_total_value ?? chartTotal);
  const deposits = Number(row.deposit_total ?? byKey.deposit_total ?? 0);
  const withdrawals = Number(row.withdrawal_total ?? byKey.withdrawal_total ?? 0);

  return (
    <div
      className="bg-white border rounded shadow-sm p-2"
      style={{ minWidth: '270px', fontSize: '0.82rem' }}
    >
      <div className="fw-semibold mb-1">{formatShortDate(row.date || label)}</div>
      <div className="small text-muted">Visible Stack Total</div>
      <div className="fw-semibold mb-1">{fmtCurrencyPrecise(chartTotal)}</div>
      {chartTotal !== familyTotal && (
        <>
          <div className="small text-muted">Family Total</div>
          <div className="fw-semibold mb-1">{fmtCurrencyPrecise(familyTotal)}</div>
        </>
      )}

      <div className="d-flex gap-3 mb-1">
        <div>
          <span className="small text-muted d-block">Deposits</span>
          <span className="text-success fw-semibold">{fmtCurrencyPrecise(deposits)}</span>
        </div>
        <div>
          <span className="small text-muted d-block">Withdrawals</span>
          <span className="text-danger fw-semibold">{fmtCurrencyPrecise(withdrawals)}</span>
        </div>
      </div>

      {accountRows.length > 0 && (
        <div className="mt-2" style={{ maxHeight: '180px', overflowY: 'auto' }}>
          {accountRows.map((row) => (
            <div key={row.accountNumber} className="d-flex justify-content-between align-items-center gap-2">
              <span className="d-flex align-items-center gap-1">
                <span
                  style={{
                    width: '8px',
                    height: '8px',
                    borderRadius: '50%',
                    backgroundColor: row.color,
                    display: 'inline-block',
                  }}
                ></span>
                <span>{row.label}</span>
              </span>
              <span className="fw-semibold">{fmtCurrencyPrecise(row.value)}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const FamilyHoldingsHistoryChart = ({
  dailyPoints = [],
  events = [],
  visibleAccounts = [],
  familyFullName = '',
  selectedAllAccounts = true,
}) => {
  const accountMeta = useMemo(() => {
    const out = {};
    visibleAccounts.forEach((account, idx) => {
      out[account.account_number] = {
        label: account.account_name || account.account_number,
        color: COLORS[idx % COLORS.length],
      };
    });
    return out;
  }, [visibleAccounts]);

  const accountKeys = useMemo(
    () => visibleAccounts.map((account) => account.account_number),
    [visibleAccounts]
  );

  const legendItems = useMemo(
    () => accountKeys.map((accountNumber) => ({
      accountNumber,
      key: `acct_${accountNumber}`,
      label: accountMeta[accountNumber]?.label || accountNumber,
      color: accountMeta[accountNumber]?.color || '#6c757d',
    })),
    [accountKeys, accountMeta]
  );

  // Legend-driven show/hide, keyed by account number so it survives re-sorts.
  const [hiddenAccounts, setHiddenAccounts] = useState(() => new Set());

  const toggleAccount = (accountNumber) => {
    setHiddenAccounts((prev) => {
      const next = new Set(prev);
      if (next.has(accountNumber)) next.delete(accountNumber);
      else next.add(accountNumber);
      return next;
    });
  };

  const eventByDate = useMemo(() => {
    const out = {};
    const pointDates = (dailyPoints || [])
      .map((point) => String(point?.date || ''))
      .filter(Boolean)
      .sort();

    if (pointDates.length === 0) return out;

    const bucketForEventDate = (eventDate) => {
      // Holdings backfills can be sparse (e.g. 1st/15th only). Place each
      // event on the first holdings date at/after the trade date so markers
      // remain visible even when there is no exact date match.
      const match = pointDates.find((pointDate) => pointDate >= eventDate);
      return match || pointDates[pointDates.length - 1];
    };

    events.forEach((event) => {
      const rawDate = String(event?.date || '');
      if (!rawDate) return;
      const bucketDate = bucketForEventDate(rawDate);
      const existing = out[bucketDate] || {
        date: bucketDate,
        deposit_total: 0,
        withdrawal_total: 0,
        net_total: 0,
        account_events: {},
      };

      const mergedAccountEvents = { ...(existing.account_events || {}) };
      const accountEvents = Array.isArray(event?.account_events)
        ? event.account_events
        : [];
      accountEvents.forEach((accountEvent) => {
        const accountNumber = String(accountEvent?.account_number || '').trim();
        if (!accountNumber) return;
        const prev = mergedAccountEvents[accountNumber] || {
          deposit_total: 0,
          withdrawal_total: 0,
          net_total: 0,
        };
        mergedAccountEvents[accountNumber] = {
          deposit_total: Number(prev.deposit_total || 0) + Number(accountEvent?.deposit_total || 0),
          withdrawal_total:
            Number(prev.withdrawal_total || 0) + Number(accountEvent?.withdrawal_total || 0),
          net_total: Number(prev.net_total || 0) + Number(accountEvent?.net_total || 0),
        };
      });

      out[bucketDate] = {
        date: bucketDate,
        deposit_total: Number(existing.deposit_total || 0) + Number(event?.deposit_total || 0),
        withdrawal_total:
          Number(existing.withdrawal_total || 0) + Number(event?.withdrawal_total || 0),
        net_total: Number(existing.net_total || 0) + Number(event?.net_total || 0),
        account_events: mergedAccountEvents,
      };
    });

    return out;
  }, [events, dailyPoints]);

  const chartData = useMemo(() => {
    return (dailyPoints || []).map((point) => {
      const row = {
        date: point.date,
        date_ts: isoDateToUtcTimestamp(point.date),
        family_total_value: Number(point.family_total_value || 0),
        family_total_cash: Number(point.family_total_cash || 0),
        account_values: {},
      };

      accountKeys.forEach((accountNumber) => {
        const value = valueForAccount(point, accountNumber);
        row.account_values[accountNumber] = value;
        row[`acct_${accountNumber}`] = value;
      });

      // Hidden accounts drop out of the visible stack total, not just the area fill.
      row.chart_total_value = accountKeys.reduce(
        (sum, accountNumber) =>
          hiddenAccounts.has(accountNumber) ? sum : sum + Number(row[`acct_${accountNumber}`] || 0),
        0
      );

      const event = eventByDate[point.date] || {};
      row.deposit_total = Number(event.deposit_total || 0);
      row.withdrawal_total = Number(event.withdrawal_total || 0);
      row.deposit_marker = row.deposit_total > 0 ? row.chart_total_value : null;
      row.withdrawal_marker = row.withdrawal_total > 0 ? row.chart_total_value : null;

      const eventByAccount = event.account_events || {};
      let cumulativeTop = 0;
      accountKeys.forEach((accountNumber) => {
        if (hiddenAccounts.has(accountNumber)) return;
        const value = Number(row[`acct_${accountNumber}`] || 0);
        cumulativeTop += value;
        const accountEvent = eventByAccount[accountNumber] || {};
        row[`deposit_marker_${accountNumber}`] =
          Number(accountEvent.deposit_total || 0) > 0 ? cumulativeTop : null;
        row[`withdrawal_marker_${accountNumber}`] =
          Number(accountEvent.withdrawal_total || 0) > 0 ? cumulativeTop : null;
      });

      return row;
    });
  }, [dailyPoints, accountKeys, eventByDate, hiddenAccounts]);

  const xTicks = useMemo(() => {
    const validTs = chartData
      .map((row) => row.date_ts)
      .filter((ts) => Number.isFinite(ts));
    if (validTs.length === 0) return [];
    const minTs = Math.min(...validTs);
    const maxTs = Math.max(...validTs);
    return monthStartTicks(minTs, maxTs);
  }, [chartData]);

  const hasData = chartData.length > 0;

  if (!hasData) {
    return (
      <div className="text-muted small py-4 text-center">
        No holdings history found for the selected date window.
      </div>
    );
  }

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 mb-2">
        <div>
          <div className="fw-semibold">Family Holdings Over Time</div>
          <div className="text-muted small">
            {selectedAllAccounts ? 'All family accounts' : 'Filtered account'} for {familyFullName}
          </div>
        </div>
        <div className="text-muted small d-flex gap-3">
          <span><i className="bi bi-circle-fill text-success me-1"></i>Deposit event</span>
          <span><i className="bi bi-circle-fill text-danger me-1"></i>Withdrawal event</span>
        </div>
      </div>

      <div className="d-flex flex-wrap align-items-center gap-3 mb-2 small">
        {legendItems.map((item) => {
          const isHidden = hiddenAccounts.has(item.accountNumber);
          return (
            <button
              key={item.key}
              type="button"
              className="btn btn-link p-0 text-decoration-none"
              onClick={() => toggleAccount(item.accountNumber)}
              style={{ color: 'inherit' }}
            >
              <span className="d-inline-flex align-items-center gap-1">
                <span
                  style={{
                    width: '10px',
                    height: '10px',
                    borderRadius: '50%',
                    backgroundColor: item.color,
                    opacity: isHidden ? 0.35 : 1,
                    display: 'inline-block',
                  }}
                ></span>
                <span
                  style={{
                    textDecoration: isHidden ? 'line-through' : 'none',
                    opacity: isHidden ? 0.5 : 1,
                  }}
                >
                  {item.label}
                </span>
              </span>
            </button>
          );
        })}
      </div>

      <div style={{ width: '100%', height: 360 }}>
        <ResponsiveContainer>
          <ComposedChart data={chartData} margin={{ top: 8, right: 18, left: 8, bottom: 8 }}>
            <CartesianGrid strokeDasharray="3 3" opacity={0.45} />
            <XAxis
              dataKey="date_ts"
              type="number"
              scale="time"
              domain={['dataMin', 'dataMax']}
              ticks={xTicks}
              tickFormatter={formatShortDate}
              minTickGap={20}
            />
            <YAxis tickFormatter={formatCurrencyNoCents} width={104} />
            <Tooltip
              content={
                <ChartTooltip
                  accountMeta={accountMeta}
                  accountKeys={accountKeys}
                />
              }
            />

            {accountKeys.map((accountNumber) => {
              const key = `acct_${accountNumber}`;
              const account = accountMeta[accountNumber];
              return (
                <Area
                  key={key}
                  type="monotone"
                  dataKey={key}
                  name={account?.label || accountNumber}
                  stackId="family"
                  stroke={account?.color || '#6c757d'}
                  fill={account?.color || '#6c757d'}
                  fillOpacity={0.24}
                  hide={hiddenAccounts.has(accountNumber)}
                  isAnimationActive={false}
                />
              );
            })}

            <Line
              type="monotone"
              dataKey="chart_total_value"
              name="Visible stack total"
              stroke="#0b2239"
              strokeWidth={2.2}
              dot={false}
              isAnimationActive={false}
            />

            <Scatter
              dataKey="deposit_marker"
              name="Deposit event"
              fill="#198754"
              shape="circle"
              legendType="none"
              hide
              isAnimationActive={false}
            />
            <Scatter
              dataKey="withdrawal_marker"
              name="Withdrawal event"
              fill="#dc3545"
              shape="circle"
              legendType="none"
              hide
              isAnimationActive={false}
            />

            {accountKeys.map((accountNumber) => (
              <Scatter
                key={`dep-${accountNumber}`}
                dataKey={`deposit_marker_${accountNumber}`}
                name="Deposit event"
                fill="#198754"
                shape="circle"
                legendType="none"
                isAnimationActive={false}
              />
            ))}

            {accountKeys.map((accountNumber) => (
              <Scatter
                key={`wd-${accountNumber}`}
                dataKey={`withdrawal_marker_${accountNumber}`}
                name="Withdrawal event"
                fill="#dc3545"
                shape="circle"
                legendType="none"
                isAnimationActive={false}
              />
            ))}
          </ComposedChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
};

export default FamilyHoldingsHistoryChart;
